<script lang="ts" setup>
import { computed, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'

import RegionPicker from '@/components/Footer/RegionPicker.vue'
import SocialsWrapper from '@/components/Footer/SocialsWrapper.vue'
import { isSponsorshipFortalezaPresent } from '@/services/navigationService'
import { useFooterData } from '@/services/seoGlobalService'

import ColumnOne from './FooterUpperComponents/ColumnOne.vue'
import ColumnThree from './FooterUpperComponents/ColumnThree.vue'
import ColumnTwo from './FooterUpperComponents/ColumnTwo.vue'
import CompanyContacts from './FooterUpperComponents/CompanyContacts.vue'

const route = useRoute()
const lang = computed(() => route.params.lang || 'en')

const propTradingLang = computed(() => (lang.value === 'pt' ? 'pt-pt' : (lang.value === 'zh-hans' || lang.value === 'zh-hant') ? 'en' : lang.value))

const isPublished = isSponsorshipFortalezaPresent()

const upperFooterData = useFooterData()

declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (element: HTMLElement | null, refresh: boolean) => void
    }
  }
}

const loadTrustpilot = () => {
  if (window.Trustpilot) {
    window.Trustpilot.loadFromElement(document.getElementById('trustpilot-widget'), true)
  } else {
    console.warn('Trustpilot script is not yet loaded.')
  }
}

onMounted(() => {
  nextTick(() => {
    loadTrustpilot()
  })
})
</script>

<template>
  <div class="1440:pb-[56px] w-full flex flex-col items-center" v-bind="$attrs">
    <div class="w-full max-w-[1920px]">
      <div class="flex flex-col items-center gap-10 md:gap-0 md:flex-row lg:px-[20px] px-[10px]">
        <SocialsWrapper
          :twitter_link="upperFooterData?.twitter_link"
          :linkedin_link="upperFooterData?.linkedin_link"
          :facebook_link="upperFooterData?.facebook_link"
          :instagram_link="upperFooterData?.instagram_link"
          :line_link="upperFooterData?.line_link"
          :youtube_link="upperFooterData?.youtube_link"
          class="order-2 md:order-none px-[10px] lg:px-0"
        ></SocialsWrapper>

        <div class="flex justify-between order-1 w-full md:order-none">
          <img
            :alt="upperFooterData?.hantec_logo_alt"
            class="h-7.5 md:h-max md:mx-auto lg:block"
            loading="lazy"
            :src="upperFooterData?.hantec_logo"
          />
          <RegionPicker :label="upperFooterData?.region_dropdown_label"></RegionPicker>
        </div>
      </div>
      <div
        class="flex-col flex 1440:flex-row mt-[67px] 3xl:mt-[51px] 1440:pl-[40px] 1440:pr-[5px] 3xl:px-[0px] lg:justify-between"
      >
        <div
          class="grid md:grid-cols-3 md:justify-center lg:grid-cols-4 1440:grid-cols-3 justify-center grid-cols-1 md:gap-[35px] lg:gap-[40px] 1200:gap-[60px] 1440:gap-[0px] 3xl:gap-[100px] gap-[60px] gap-x-[5px] lg:px-0 px-[10px] lg:inline-flex align-top w-full"
        >
          <ColumnOne
            :upperFooterData="upperFooterData"
            :propTradingLang="propTradingLang"
          ></ColumnOne>
          <ColumnTwo
            class="1440:ml-[calc(40px+(100vw-1440px)/3)] 3xl:ml-[0px]"
            :upperFooterData="upperFooterData"
          >
          </ColumnTwo>
          <ColumnThree
            class="1440:ml-[calc(60px+(100vw-1440px)/3)] 3xl:ml-[0px] lg:relative lg:left-[25px] 1440:block"
            :upperFooterData="upperFooterData"
          >
          </ColumnThree>
          <div class="hidden lg:block pr-[0px] 1440:ml-[calc(40px+(100vw-1440px)/3)] 3xl:ml-[0px]">
            <CompanyContacts :upperFooterData="upperFooterData"></CompanyContacts>
          </div>
        </div>
        <div class="flex flex-col md:items-center md:mt-[60px] lg:mt-0">
          <div class="hidden w-11/12 1440:mt-20 md:flex lg:hidden border-t-3 border-gray-350"></div>
          <div class="w-full lg:hidden">
            <CompanyContacts :upperFooterData="upperFooterData"></CompanyContacts>
          </div>
          <div class="w-full px-[10px] 1440:hidden">
            <div class="self-center w-full lg:hidden border-t-3 border-gray-350"></div>
          </div>
        </div>
        <div
          class="flex flex-col items-center justify-center w-full 1440:items-center lg:hidden 1440:flex 1440:justify-start"
        >
          <div class="flex flex-col 1440:pr-2.5">
            <img
              class="w-[250px] h-[116px] mt-10 1440:my-0 1440:relative flex-1 mb-10"
              :src="upperFooterData?.sponsor_logo"
            />
          </div>
          <div class="px-[10px] w-full">
            <div class="w-full lg:hidden md:flex 1440:hidden border-t-3 border-gray-350"></div>
          </div>
        </div>
        <div
          class="flex justify-center w-full 1440:ml-auto 1440:block 1440:w-auto mt-[40px] 1440:mt-0 lg:hidden"
        >
          <div
            id="trustpilot-widget"
            class="trustpilot-widget"
            data-businessunit-id="5f78343dacf3dd0001936c20"
            data-locale="en"
            data-review-languages="en"
            data-style-height="100%"
            data-style-width="214px"
            data-template-id="5613c9cde69ddc09340c6beb"
            data-theme="dark"
          >
            <a href="https://uk.trustpilot.com/review/hmarkets.com/" rel="noopener" target="_blank"
              >Trustpilot</a
            >
          </div>
        </div>
        <div class="justify-center hidden w-full md:flex 1440:hidden">
          <div class="block w-full mt-10 lg:hidden border-t-3 border-gray-350"></div>
        </div>
      </div>
    </div>
  </div>
</template>
